import { requestAvailabilityData, requestPriceData } from "~/domains/product/redux/actions";
import { clearCart } from "./cartPageActions";
import { type ThunkAction } from "redux-thunk";
import { type AnyAction } from "~/app/store/types";
import { batch } from "react-redux";
import { selectProductDescriptor } from "~/domains/product/redux/selectors";
import { getPackageCode, PSSizeMode, selectBestPackBasedOnStock } from "../../../../../utils/cartHelper";
import { type PriceData } from "~/domains/pricing/types/PriceData";
import { type AddToCartItem, type AddToCartResponseItem, sendBulkAddToCart as addToCartApi } from "../../api/AddToCart";
import { uniqueValues } from "~/common/utils/misc";
import { addQuantity, loadCartAmounts } from "~/features/addToCart/redux/cartAmounts/actions";
import { selectProperAmountInCart } from "~/features/addToCart/redux/cartAmounts/cartAmountSelectors";
import { type PackagingInformationEntry, type ProductData } from "~/domains/product/types/ProductData";
import { type RootState } from "~/app/store";
import {
  fakeDescriptorFromProduct,
  getNetPrice,
  getPackageCodes,
  getPriceInfo,
  getSurcharge,
  getTariff,
  isProductPMA,
} from "~/domains/product/utils/productDescriptorUtils";
import { isPackageRestrictionEnabled } from "~/features/PackageRestriction/utils/isPackageRestrictionEnabled";
import { type OrderLineItemData } from "~/domains/orders/types/modelsV2";
import { AnalyticsMQ } from "~/app/analytics/AnalyticsMQ/AnalyticsMQ";
import { zeroNetPrice } from "~/utils/PriceUtils";
import { ProductService } from "~/features/addToCart/api/ProductService";

type ThunkResult<R> = ThunkAction<R, RootState, undefined, AnyAction>;

export function orderDetailsMounted(): ThunkResult<void> {
  return (dispatch, getState) => {
    dispatch(loadCartAmounts());
  };
}

export function loadSelectedProductData(selectedEntries: OrderLineItemData[]): ThunkResult<void> {
  return (dispatch, getState) => {
    batch(() => {
      selectedEntries.forEach((entry) => {
        dispatch(requestPriceData(entry.product.code, entry.priceReference));
        dispatch(requestAvailabilityData(entry.product.code));
      });
    });
  };
}

export interface AddProductsFromOrderResult {
  entriesToAdd: AddToCartItem[];
  responseItems: AddToCartResponseItem[];
}

export function addProductsFromOrder(
  data: OrderLineItemData[],
  overrideCart?: boolean,
): ThunkResult<Promise<AddProductsFromOrderResult>> {
  return async (dispatch, getState) => {
    const productCodes = uniqueValues(data.map((orderItem) => orderItem.product.code));
    const dataPromises = data
      .map((entry) => [
        dispatch(requestPriceData(entry.product.code, entry.priceReference)),
        dispatch(requestAvailabilityData(entry.product.code)),
      ])
      .flat();

    await Promise.all<unknown>(dataPromises);

    const state = getState();

    const quantitiesToAdd: { [code: string]: number } = {};
    data.forEach((orderItem) => {
      const quantity = getQuantity(orderItem);
      const productDescriptor = selectProductDescriptor(state, {
        productCode: orderItem.product.code,
        priceReference: orderItem.priceReference,
      });
      const code = getCode(orderItem, productDescriptor.isMultiPrice);
      quantitiesToAdd[code] = (quantitiesToAdd[code] || 0) + quantity;
    });

    const entriesToAdd = transformOrderLineItemsToCartItems(data, state, overrideCart);

    if (overrideCart) {
      await dispatch(clearCart());
    }
    const responseItems = await addToCartApi(entriesToAdd);
    batch(() => {
      for (const entry of entriesToAdd) {
        dispatch(addQuantity(entry.productCode, entry.packageCode, entry.quantity, entry.priceReference));
      }
    });

    AnalyticsMQ.instance.push("addToCart", {
      products: entriesToAdd.map((entry) => {
        const product: ProductData = ProductService.getBaseProductData(entry.productCode)!;
        return {
          product,
          packaging: {
            code: entry.packageCode,
            size: entry.packageSize ?? 1,
          },
          price: {
            ...zeroNetPrice(product),
            value: entry.netPrice,
            baseValue: entry.netPrice,
          },
          quantity: entry.quantity,
        };
      }),
      context: {
        source: "unknown",
      },
    });
    return { entriesToAdd, responseItems };
  };
}

function getQuantity(orderItem: OrderLineItemData) {
  return orderItem.confirmedQty;
}

function getCode(orderItem: OrderLineItemData, is15Digit?: boolean) {
  if (is15Digit) {
    return `${orderItem.product.code}${getPackageCode(orderItem.packCode)}`;
  } else {
    return orderItem.product.code;
  }
}

export const transformOrderLineItemsToCartItems = (
  data: OrderLineItemData[],
  state: RootState,
  overrideCart?: boolean,
) => {
  const quantitiesToAdd: { [code: string]: number } = {};
  data.forEach((orderItem) => {
    const quantity = getQuantity(orderItem);
    const productDescriptor = selectProductDescriptor(state, {
      productCode: orderItem.product.code,
      priceReference: orderItem.priceReference,
    });
    const code = getCode(orderItem, productDescriptor.isMultiPrice);
    quantitiesToAdd[code] = (quantitiesToAdd[code] || 0) + quantity;
  });

  const pricesCache: {
    [code: string]: {
      price: PriceData;
      priceUnit: number;
      surcharge: number;
      tariffSurcharge: boolean;
    };
  } = {};

  const entriesToAdd: AddToCartItem[] = [];

  for (const orderItem of data) {
    const quantity = getQuantity(orderItem);
    const productCode = orderItem.product.code;
    const productDescriptor = selectProductDescriptor(state, {
      productCode,
      priceReference: orderItem.priceReference,
    });
    const packagingInformation = productDescriptor?.availability?.packagingInformation;
    let packageCode = getPackageCode(orderItem.packCode);
    // 1. Find if there is availability info for the package
    let packageInfo: PackagingInformationEntry | undefined = packagingInformation?.filter(
      (packageInfo) => packageInfo.packCode === packageCode,
    )[0];
    // 2. If the package does not exist, select one based on the default algorithm
    if (!packageInfo) {
      packageInfo = selectBestPackBasedOnStock(
        quantity,
        getPackageCodes(productDescriptor),
        productDescriptor,
        PSSizeMode.LARGEST_FIRST,
      );
      if (!packageInfo) {
        console.warn(`Could not select package for selected order item at lineNo: ${orderItem.lineNo}`);
        //return Promise.reject();
      } else {
        packageCode = packageInfo.packCode;
      }
    }
    // 3. Select the appropriate price
    const code = getCode(orderItem, productDescriptor.isMultiPrice);
    let price;
    let priceUnit;
    let surcharge;
    let tariffSurcharge = false;
    if (!pricesCache[code]) {
      // 3b. Determine and cache the price
      let totalQuantity: number;
      if (overrideCart) {
        totalQuantity = quantitiesToAdd[code];
      } else {
        const qtyInCart = selectProperAmountInCart({
          productCode,
          packCode: packageCode,
          priceReference: orderItem.priceReference,
        })(state);
        totalQuantity = quantitiesToAdd[code] + qtyInCart;
      }
      const selectPriceProps = { totalQuantity, product: productDescriptor, packCode: packageCode };
      const priceInfo = getPriceInfo(selectPriceProps);
      const selectedPrice = getNetPrice(selectPriceProps);
      const selectedSurcharge = getSurcharge(selectPriceProps);
      const selectedTariffSurcharge = getTariff(selectPriceProps.product);
      if (!selectedPrice) {
        console.warn(`Could not find price for selected order item at lineNo: ${orderItem.lineNo}`);
        // return Promise.reject();
      }
      pricesCache[code] = {
        price: selectedPrice,
        priceUnit: selectedPrice.unit,
        surcharge: selectedSurcharge,
        tariffSurcharge: selectedTariffSurcharge,
      };
      price = selectedPrice;
      priceUnit = selectedPrice.unit;
      surcharge = selectedSurcharge;
      tariffSurcharge = selectedTariffSurcharge;
    } else {
      price = pricesCache[code].price;
      priceUnit = pricesCache[code].priceUnit;
      surcharge = pricesCache[code].surcharge;
      tariffSurcharge = pricesCache[code].tariffSurcharge;
    }
    const fifteenDigit = productDescriptor.base.fifteenDigit[packageCode];

    // 4. Create a proper ATCApiEntry
    const entry: AddToCartItem = {
      productCode,
      packageSize: packageInfo?.packSize,
      packageCode,
      quantity: quantity,
      netPrice: price.value,
      priceUnit,
      comment: orderItem.comment,
      selectedDeliveryDate: null,
      selectedDeliveryDateIsManual: false,
      primaryWarehouseCode: packageInfo?.primaryWarehouseCode ?? "",
      fifteenDigit: productDescriptor.isMultiPrice || false,
      priceReference: orderItem.priceReference,
      contractLine: orderItem.contractLine || undefined,
      contractNumber: orderItem.contractNumber || undefined,
      isPMA: isProductPMA(fakeDescriptorFromProduct(orderItem.product)),

      restrictedPackage: isPackageRestrictionEnabled(),
      surcharge,
      tariffSurchargeApplicable: tariffSurcharge,
      originCountry: fifteenDigit?.countryOfOrigin ?? "",
    };
    entriesToAdd.push(entry);
  }
  return entriesToAdd;
};
